import React, { FormEvent, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useRouter } from "next/navigation";
import FilterDropdown from "./BusinessFilterDropdown";
import EFilterDropdown from "./ExperienceFilterDropdown";
import SearchIcon from "./SvgIcon";
import { GetVacancyList } from "@/services/VacancyService";
import { GetBusinessUnitDropdownList, GetCategoryDropdownList } from "@/services/DropdownService";
import useFilterJob from "@/hooks/useFilterJob";
import { FILTER_JOB } from "@/constants/LocalStorageStoreKeys";

export default function SearchJobs() {
  const router = useRouter();
  const [showDropdown, setShowDropdown] = useState<boolean>(false);

  const { job, businessUnit, experienceLevel, onSearchJobFocus } = useFilterJob((state) => state);
  const { setJob, setBusinessUnit, setExperienceLevel, setOnSearchJobFocus } = useFilterJob();

  const inputRef = useRef<HTMLInputElement>(null);

  let businessUnits = GetBusinessUnitDropdownList();
  const categories = GetCategoryDropdownList();

  const vacancies = GetVacancyList();

  businessUnits = businessUnits?.sort((a) => {
    return a.name.toLowerCase() === "All Business Unit".toLowerCase() ? -1 : 1;
  });

  const vacancyItems = useMemo(() => {
    return (
      vacancies?.vacancies?.map((vacancies) => ({
        id: vacancies.id,
        title: vacancies.title,
        businessUnit: vacancies.businessUnitName,
        category: vacancies.categoryName,
      })) || []
    );
  }, [vacancies]);

  useEffect(() => {
    if (onSearchJobFocus && inputRef.current) {
      inputRef.current.focus();
    }
    return () => localStorage.removeItem(FILTER_JOB);
  }, [onSearchJobFocus]);

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    router.push(`/vacancy`);
  };

  const handleChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
      setOnSearchJobFocus(true);
      const { name, value } = event.target;

      switch (name) {
        case "job":
          setJob(value);
          break;
        case "businessUnit":
          setBusinessUnit(value);
          break;
        case "experienceLevel":
          setExperienceLevel(value);
          break;
      }
      router.push(`/vacancy`);
    },
    [router, setJob, setBusinessUnit, setExperienceLevel, setOnSearchJobFocus]
  );

  const filteredVacancies = useMemo(
    () =>
      vacancyItems.filter((item) =>
        item.title.toLowerCase().includes(job.toLowerCase())
      ),
    [vacancyItems, job]
  );

  const highlightTxt = useCallback((text, searchTerm) => {
    if (!searchTerm) return text;
    const regex = new RegExp(`(${searchTerm})`, 'gi');
    return text.split(regex).map((txt, index) =>
      regex.test(txt) ? (
        <span key={index} className="font-bold text-[#93885B]">
          {txt}
        </span>
      ) : (
        txt
      )
    );
  }, []);

  useEffect(() => {
    const handleOutsideClick = (event) => {
      const dropdown = document.getElementById('search-job-form-id');
      if (showDropdown && dropdown && !dropdown.contains(event.target)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleOutsideClick);
    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
    };
  }, [showDropdown]);

  return (
    <div className="lg:flex">
      <form
        id="search-job-form-id"
        className="flex flex-grow items-center space-x-2 border-[3px] border-primary"
        onSubmit={handleSubmit}
      >
        <div className="grow">
          <div className="relative">
            <div className="absolute inset-y-0 start-0 flex items-center ps-3 pointer-events-none">
              <SearchIcon className="w-3 h-3 text-black" />
            </div>
            <input
              ref={inputRef}
              className="flex-grow md:text-lg px-4 py-2 ps-12 bg-transparent focus:outline-none w-full placeholder-gray-500 placeholder-opacity-50 placeholder:italic"
              type="text"
              placeholder="Discover the employment opportunity  "
              name="job"
              value={job}
              onChange={(e) => {
                //setShowDropdown(true);
                handleChange(e);
              }}
            />

            {showDropdown && job !== '' && (
              <ul className="z-10 border border-gray-300 rounded mt-1 bg-white w-full absolute max-h-56 overflow-y-auto custom-scrollbar scrollbar">
                <h2 className="pl-5 py-2 font-semibold text-gray-500 text-sm">
                  Search Recommendation:
                </h2>
                {filteredVacancies.length > 0 ? (
                  filteredVacancies.map((item) => (
                    <li
                      key={item.id}
                      className="p-2 hover:bg-gray-200 cursor-pointer line-clamp-1 flex items-center gap-3 px-5"
                      onMouseDown={(e) => {
                        e.preventDefault();
                        router.push(`/vacancy/detail/${item.id}`)
                        setShowDropdown(false);
                      }}
                    >
                      <div className="flex items-center gap-3 hover:text-current">
                        <SearchIcon className="w-3 h-3 text-black" />
                        <span>
                          {item.businessUnit} - {highlightTxt(item.title, job)} ({item.category})
                        </span>
                      </div>
                    </li>
                  ))
                ) : (
                  <li className="p-2 hover:bg-gray-200 cursor-pointer line-clamp-1 flex items-center gap-3 px-5">
                    <SearchIcon className="w-3 h-3 text-black" />
                    <span>No Result</span>
                  </li>
                )}
                <hr className="border-gray-300" />
              </ul>
            )}
          </div>
        </div>
      </form>
      <div className="flex flex-col-reverse sm:flex-row lg:flex-grow">
        <FilterDropdown
          businessUnitOptions={businessUnits || []}
          formData={{businessUnit: businessUnit}}
          handleChange={handleChange}
          borderType={1}
        />
        <EFilterDropdown
          experienceLevelOptions={categories || []}
          formData={{experienceLevel: experienceLevel}}
          handleChange={handleChange}
        />
      </div>
      <style jsx>{`
        .custom-scrollbar::-webkit-scrollbar {
          width: 8px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb {
          background-color: rgba(0, 0, 0, 0.2);
          border-radius: 10px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover {
          background-color: rgba(0, 0, 0, 0.3);
        }
        .custom-scrollbar::-webkit-scrollbar-track {
          background: transparent;
        }
      `}</style>
    </div>
  );
}
